import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Clock, AlertCircle, ArrowRight } from "lucide-react";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";

export function SessionExpired() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen relative flex items-center justify-center overflow-hidden px-4"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <AnimatedBackground />

      {/* Partículas de fondo flotando */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: [5, 7, 4, 8, 6, 5, 7, 4][i],
            height: [5, 7, 4, 8, 6, 5, 7, 4][i],
            top: [`15%`, `68%`, `28%`, `82%`, `38%`, `12%`, `52%`, `24%`][i],
            left: [`6%`, `14%`, `80%`, `84%`, `8%`, `88%`, `92%`, `42%`][i],
            background: i % 2 === 0 ? "rgba(37, 99, 235, 0.3)" : "rgba(124, 58, 237, 0.3)",
            boxShadow: i % 2 === 0 ? "0 0 10px rgba(37, 99, 235, 0.4)" : "0 0 10px rgba(124, 58, 237, 0.4)",
          }}
          animate={{
            y: [0, -22, 0],
            x: [0, [-3, 4, -5, 3, -4, 5, -3, 4][i], 0],
            opacity: [0.25, 0.8, 0.25],
            scale: [1, 1.25, 1],
          }}
          transition={{
            duration: [5.5, 6.2, 4.8, 7.2, 5.8, 6.8, 4.4, 5.8][i],
            repeat: Infinity,
            ease: "easeInOut",
            delay: [0.1, 0.9, 0.3, 2.0, 1.3, 0.7, 2.4, 1.0][i],
          }}
        />
      ))}

      {/* Tarjeta de Sesión Expirada Centrada (Formato Compacto) */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-[365px] bg-white/80 backdrop-blur-md rounded-3xl overflow-hidden border border-slate-100/50 shadow-[0_12px_30px_rgba(0,0,0,0.05)] text-center relative z-10"
      >
        {/* Cabecera temática con degradado nocturno */}
        <div className="bg-gradient-to-r from-[#1e1b4b] to-[#312e81] py-6 px-5 text-white relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent pointer-events-none" />

          {/* Icono de Reloj animado compacto */}
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/20 shadow-inner mb-2.5 animate-pulse">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-lg font-black tracking-tight mb-1">
            Sesión Expirada
          </h2>
          <p className="text-[11px] font-semibold text-slate-200/80 max-w-[240px] mx-auto leading-relaxed">
            Tu sesión ha caducado por inactividad
          </p>
        </div>

        {/* Cuerpo del aviso */}
        <div className="p-5 space-y-4.5 text-left">
          {/* Caja de alerta sutil compacta */}
          <div className="flex gap-2.5 p-3.5 rounded-xl bg-red-50/70 border border-red-100/50 shadow-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-500 mt-0.5" />
            <div className="space-y-0.5">
              <p className="text-xs font-bold text-red-950">Tiempo de sesión agotado</p>
              <p className="text-[10px] font-semibold text-red-700/90 leading-normal">
                Por tu seguridad, tu sesión ha expirado. Por favor, ingresa nuevamente.
              </p>
            </div>
          </div>

          {/* Consejos de seguridad compactos */}
          <div className="p-4 rounded-xl bg-slate-50/50 border border-slate-100/50 space-y-2.5">
            <h3 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
              Consejos de seguridad:
            </h3>
            <ul className="space-y-2">
              {[
                "Cierra sesión cuando termines de usar la app",
                "No compartas tus datos de acceso",
                "Usa claves seguras y únicas",
              ].map((tip, index) => (
                <li key={index} className="flex items-start gap-2 text-[10px] font-semibold text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-900 mt-1 flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Botón de acción compacto */}
          <motion.button
            onClick={() => navigate("/login")}
            className="group w-full py-3 rounded-xl text-xs font-bold text-white relative overflow-hidden flex items-center justify-center gap-1.5 transition-all shadow-[0_4px_12px_rgba(49,46,129,0.2)]"
            style={{
              background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
            }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)",
              }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
            />
            <span>Iniciar Sesión Nuevamente</span>
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </motion.button>


        </div>
      </motion.div>
    </div>
  );
}

export default SessionExpired;
